import { useCallback, useEffect, useState } from "react";

import { useLocation } from "react-router-dom";
import CheckIcon from "@mui/icons-material/Check";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid2";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import FlexsaveStorageImg from "../../assets/flexsave/flexsave-for-storage.svg";
import { useLocalStorage } from "../../Components/hooks/storageHooks";
import { useErrorSnackbar } from "../../Components/SharedSnackbar/SharedSnackbar.context";
import { useCustomerContext } from "../../Context/CustomerContext";
import useSegmentTrackEvent from "../../utils/useSegmentTrackEvent";
import useCreateTicket from "../MPA/hooks/useCreateTicket";

const NAV_LINK_EVENT = "Flexsave Storage Opened";
const CTA_SUCCESS_EVENT = "Flexsave Storage Register Interest Completed";

const FlexsaveStorage = () => {
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const [hasTrackedNav, setHasTrackedNav] = useState(false);
  const { createTicket } = useCreateTicket();
  const { customer } = useCustomerContext({ allowNull: true });
  const showError = useErrorSnackbar();
  const { trackEvent } = useSegmentTrackEvent();
  const [hasRegisteredInterest, setHasRegisteredInterest] = useLocalStorage<boolean>("flexsave-storage-store", false);

  useEffect(() => {
    if (!hasTrackedNav) {
      trackEvent(NAV_LINK_EVENT);
      setHasTrackedNav(true);
    }
  }, [location.pathname, hasTrackedNav, trackEvent]);

  const handleRegisterInterest = useCallback(async () => {
    if (!customer?.id) {
      showError("Customer information is missing. Please try again later.");
      return;
    }
    setLoading(true);
    try {
      await createTicket(customer.id, {
        subject: "Register interest for Flexsave for Storage",
        description:
          "Customer is interested in early access to Flexsave for Storage. Please reach out with more information.",
        priority: "normal",
        platformField: "doit",
        tags: ["upsell", "register-interest", "flexsave-storage"],
      });
      setHasRegisteredInterest(true);
      trackEvent(CTA_SUCCESS_EVENT, { pathname: location.pathname });
    } catch (error) {
      showError("An error occurred while sending the registration request.");
    }
    setLoading(false);
  }, [customer, createTicket, location.pathname, showError, trackEvent, setHasRegisteredInterest]);

  return (
    <Container maxWidth="xl">
      <Grid container spacing={6} sx={{ mt: 8 }}>
        <Grid size={{ xs: 12, md: 5 }} offset={{ xs: 0, md: 1 }} sx={{ mb: 4 }}>
          <Stack alignItems="center" justifyContent="space-between">
            <Stack>
              <Typography variant="h1" color="text.primary" sx={{ mb: 2 }}>
                Flexsave for Storage
              </Typography>
              <Typography variant="h3" color="text.primary" sx={{ mb: 2 }}>
                Early access
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Soon we'll extend autonomous, workload-aware cost optimization directly to your cloud storage!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Flexsave for Storage continuously analyzes your object usage patterns, intelligently migrates data
                across storage classes to minimize costs, and surfaces actionable recommendations—without manual
                lifecycle policies or scripting overhead.
              </Typography>
            </Stack>
            {hasRegisteredInterest ? (
              <Stack direction="row" alignSelf="flex-start" spacing={1} sx={{ mt: 1 }}>
                <Stack alignSelf="flex-start">
                  <CheckIcon
                    sx={{ color: "success.main", fontSize: 16, mt: 0.25, alignSelf: "flex-start" }}
                    data-cy="flexsave-storage-success-icon"
                  />
                </Stack>
                <Typography variant="caption" color="success.main" data-cy="flexsave-storage-success-msg">
                  Thanks for registering your interest. Our R&D team will reach out to you shortly.
                </Typography>
              </Stack>
            ) : (
              <Button
                variant="contained"
                loading={loading}
                sx={{ alignSelf: "flex-start" }}
                size="large"
                data-cy="flexsave-storage-register-interest-btn"
                onClick={handleRegisterInterest}
                disabled={loading}
              >
                Register interest
              </Button>
            )}
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }} offset={{ xs: 0 }}>
          <Stack sx={{ mb: 3 }}>
            <img src={FlexsaveStorageImg} alt="flexsave-storage" width="100%" />
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
};

export default FlexsaveStorage;
